import { businessDetailQueryOptions, businessPhotosInfiniteQueryOptions } from '@/api/queries/businesses';
import { ImageViewer } from '@/components/display/image-viewer';
import { ScreenHeader } from '@/components/layout/screen-header';
import { AppImage } from '@/components/ui/app-image';
import { toBusinessViewerPhotos } from '@/features/business/business-viewer-photos';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  View,
  useWindowDimensions,
  type LayoutChangeEvent,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const NUM_COLUMNS = 4;

export function BusinessPhotosScreen() {
  const theme = useAppTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { width: windowWidth } = useWindowDimensions();
  const [gridWidth, setGridWidth] = useState<number | null>(null);

  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const { data: business } = useQuery(businessDetailQueryOptions(id || ''));

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(businessPhotosInfiniteQueryOptions(id || ''));

  const allPhotos = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );
  // Captioned with each photo's check-in so the lightbox can show and link it.
  const viewerPhotos = useMemo(() => toBusinessViewerPhotos(allPhotos), [allPhotos]);

  // Cells are square, so a row is one cell tall. Measure the grid itself:
  // on desktop web the sidebar and content-width clamps make it narrower than
  // the window. Fall back to the window width until the first layout.
  const rowHeight = (gridWidth ?? windowWidth) / NUM_COLUMNS;

  const handleGridLayout = useCallback((event: LayoutChangeEvent) => {
    setGridWidth(event.nativeEvent.layout.width);
  }, []);

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({ length: rowHeight, offset: rowHeight * index, index }),
    [rowHeight],
  );

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const openViewer = useCallback((index: number) => {
    setViewerIndex(index);
    setViewerVisible(true);
  }, []);

  const markFailed = useCallback((photoId: string) => {
    setFailedIds((prev) => new Set(prev).add(photoId));
  }, []);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof allPhotos)[number]; index: number }) => (
      <View style={{ width: `${100 / NUM_COLUMNS}%`, aspectRatio: 1, padding: 3 }}>
        <Pressable
          testID={`business-photo-${item.id}`}
          style={{ flex: 1, borderRadius: 8, overflow: 'hidden' }}
          onPress={() => openViewer(index)}
        >
          {failedIds.has(item.id) ? (
            <View
              testID={`business-photo-${item.id}-fallback`}
              className="bg-surface-secondary items-center justify-center"
              style={{ width: '100%', height: '100%' }}
            >
              <Ionicons name="image-outline" size={28} className="text-muted-foreground" />
            </View>
          ) : (
            <AppImage
              r2Key={item.r2Key || item.imageUrl}
              preset="thumbnail"
              recyclingKey={item.id}
              style={{ width: '100%', height: '100%' }}
              accessibilityLabel={item.altText?.trim() || `Photo ${index + 1}`}
              onError={() => markFailed(item.id)}
            />
          )}
        </Pressable>
      </View>
    ),
    [failedIds, openViewer, markFailed],
  );

  const ListFooterComponent = isFetchingNextPage ? (
    <View className="py-4 items-center">
      <ActivityIndicator color={theme.colors.primary} />
    </View>
  ) : null;

  return (
    <SafeAreaView className="flex-1 bg-background" edges={['top']}>
      <ScreenHeader
        title={business?.name ?? 'Photos'}
        showBackButton
      />

      <FlatList
        testID="business-photos-grid"
        data={allPhotos}
        numColumns={NUM_COLUMNS}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        extraData={failedIds}
        getItemLayout={getItemLayout}
        onLayout={handleGridLayout}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        ListFooterComponent={ListFooterComponent}
        showsVerticalScrollIndicator={false}
      />

      <ImageViewer
        photos={viewerPhotos}
        initialIndex={viewerIndex}
        visible={viewerVisible}
        onClose={() => setViewerVisible(false)}
      />
    </SafeAreaView>
  );
}
